"use client";

import Link from "next/link";
import type {
  CBrowserPressOnlineContent,
  CBrowserPressOnlineItem,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { CatalogLoadMore } from "@/components/cms/Pagination/CatalogLoadMore";
import {
  cBrowserPressOnlineTranslations,
  type CBrowserPressOnlineTranslations,
} from "./CBrowserPressOnline.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import {
  CBrowserPressOnlineArticle,
  formatAppliesLine,
} from "./CBrowserPressOnlineArticle";
import styles from "./CBrowserPressOnline.module.css";

/**
 * CBrowserPressOnline - visitor view komponentu `c_browser_press_online`
 * (Przegladarka prasy online - gazetka, katalog, oferta).
 *
 * **Data source:** `C_BROWSER_PRESS_ONLINE_CONTROLLER::getApiContent()` zwraca:
 *   ```
 *   list:  { id, symbol, view:'list', items:[{ id, name, text, cover, ... }], pagination }
 *   issue: { id, symbol, view:'issue', issue:{ id, name, images:[...], file, ... }, list_href }
 *   ```
 *
 * **Dwa tryby (discriminator `content.view`):**
 *   - `list` - paginowana siatka wydan; kazdy kafel linkuje do `item.href`.
 *   - `issue` - patrz `CBrowserPressOnlineArticle.tsx`.
 */
export function CBrowserPressOnline({ content }: CBrowserPressOnlineProps) {
  const t = useT(cBrowserPressOnlineTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyData}</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {content.view === "issue" && content.issue ? (
        <CBrowserPressOnlineArticle
          issue={content.issue}
          listHref={content.list_href ?? null}
        />
      ) : (
        <ListView content={content} t={t} />
      )}
    </div>
  );
}

export type CBrowserPressOnlineProps = {
  content: CBrowserPressOnlineContent;
};

/* === List view (siatka wydan) ============================================ */

function ListView({
  content,
  t,
}: {
  content: CBrowserPressOnlineContent;
  t: CBrowserPressOnlineTranslations;
}) {
  const items: CBrowserPressOnlineItem[] = Array.isArray(content.items)
    ? content.items
    : [];
  const pagination = content.pagination;

  if (items.length === 0) {
    return <div className={styles.empty}>{t.emptyList}</div>;
  }

  return (
    <CatalogLoadMore<CBrowserPressOnlineItem>
      initialItems={items}
      initialPage={pagination?.page ?? 1}
      totalPages={pagination?.total_pages ?? 1}
      totalCount={pagination?.total_count}
      getKey={(item) => item.id}
      extractItems={(c) => {
        const arr = (c as CBrowserPressOnlineContent | null)?.items;
        return Array.isArray(arr) ? arr : [];
      }}
    >
      {(allItems) => (
        <ul className={styles.issues}>
          {allItems.map((item) => (
            <IssueCard key={item.id} item={item} t={t} />
          ))}
        </ul>
      )}
    </CatalogLoadMore>
  );
}

function IssueCard({
  item,
  t,
}: {
  item: CBrowserPressOnlineItem;
  t: CBrowserPressOnlineTranslations;
}) {
  const href = extractInternalHref(item.href);
  const appliesLine = formatAppliesLine(
    item.applies_date_start,
    item.applies_date_end,
    t,
  );

  const inner = (
    <>
      <div className={styles.coverWrap}>
        {item.cover ? (
          /* Native <img> - cross-origin URL z Imperial backendu. */
          <img
            src={item.cover.src}
            alt={item.name}
            className={styles.cover}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span className={styles.coverPlaceholder} aria-hidden="true" />
        )}
        {item.count_pictures > 0 && (
          <span className={styles.countBadge}>
            {t.pagesCount(item.count_pictures)}
          </span>
        )}
      </div>
      <div className={styles.issueCardBody}>
        <h2 className={styles.issueName}>{item.name}</h2>
        {appliesLine && <p className={styles.issueDates}>{appliesLine}</p>}
      </div>
    </>
  );

  return (
    <li className={styles.issueCard}>
      {href !== null ? (
        <Link href={href} className={styles.issueCardLink} aria-label={item.name}>
          {inner}
        </Link>
      ) : (
        <a href={item.href} className={styles.issueCardLink} aria-label={item.name}>
          {inner}
        </a>
      )}
    </li>
  );
}
